import { FriendlyCaptchaSDK } from "@friendlycaptcha/sdk";

/**
 * Friendly Captcha handler.
 *
 * Replaces the old Google reCAPTCHA v3 inline scripts on the theme-native forms
 * (_parts/elements/contact-forms/*). It:
 *   - mounts a widget on every `.frc-captcha[data-sitekey]` element
 *   - intercepts the submit of every `form[data-frc-form]`, sends it over AJAX,
 *     and swaps in the success / error message (or redirects if `data-redirect`)
 *
 * `init()` runs on first load and after every Taxi page transition;
 * `destroy()` tears widgets and listeners down before leaving a page.
 */

const sdk = new FriendlyCaptchaSDK();

const UNSOLVED_PREFIX = "."; // ".UNSTARTED", ".ERROR", ".EXPIRED", ...

export default class FriendlyCaptchaHandler {
	constructor() {
		this.widgets = [];
		this.forms = [];
	}

	init() {
		document
			.querySelectorAll(".frc-captcha[data-sitekey]")
			.forEach((el) => this.mountWidget(el));

		document
			.querySelectorAll("form[data-frc-form]")
			.forEach((form) => this.bindForm(form));
	}

	mountWidget(el) {
		if (el.dataset.frcMounted) return;
		el.dataset.frcMounted = "1";

		const widget = sdk.createWidget({
			element: el,
			sitekey: el.dataset.sitekey,
			startMode: el.dataset.start || "auto",
		});

		this.widgets.push({ el, widget });
	}

	bindForm(form) {
		if (form.dataset.frcBound) return;
		form.dataset.frcBound = "1";

		const handler = (e) => this.onSubmit(e, form);
		form.addEventListener("submit", handler);
		this.forms.push({ form, handler });
	}

	widgetForForm(form) {
		const el = form.querySelector(".frc-captcha");
		return el ? this.widgets.find((w) => w.el === el) : null;
	}

	onSubmit(e, form) {
		e.preventDefault();

		if (!form.reportValidity()) return;

		const submitBtn = form.querySelector('[type="submit"]');
		const responseField = form.querySelector('[name="frc-captcha-response"]');
		const entry = this.widgetForForm(form);

		// A widget is present but hasn't produced a valid solution yet.
		if (entry && !isSolved(responseField && responseField.value)) {
			this.showMessage(
				form,
				"error",
				"Please wait for the security check to finish, then submit again."
			);
			try {
				entry.widget.start();
			} catch (err) {
				/* start() is a no-op if already running */
			}
			return;
		}

		if (submitBtn) submitBtn.disabled = true;
		this.clearMessage(form);

		fetch(form.getAttribute("action"), {
			method: "POST",
			headers: { "X-Requested-With": "XMLHttpRequest" },
			body: new FormData(form),
		})
			.then((r) => r.json())
			.then((res) => {
				if (res.success) {
					const redirect = form.dataset.redirect;
					if (redirect) {
						window.location.href = redirect;
						return;
					}
					form.parentElement.innerHTML =
						'<div class="col-span-full text-dark-green form-message  form-message--success" role="alert" aria-live="polite">' +
						res.data.message +
						"</div>";
				} else {
					if (submitBtn) submitBtn.disabled = false;
					this.showMessage(
						form,
						"error",
						res.data && res.data.message
							? res.data.message
							: "Something went wrong. Please try again."
					);
					if (entry) {
						try {
							entry.widget.reset();
						} catch (err) {
							/* noop */
						}
					}
				}
			})
			.catch(() => {
				if (submitBtn) submitBtn.disabled = false;
				this.showMessage(
					form,
					"error",
					"Something went wrong. Please try again."
				);
			});
	}

	showMessage(form, type, text) {
		let el = form.querySelector(".form-message--" + type);
		if (!el) {
			el = document.createElement("div");
			el.className =
				"col-span-full text-dark-green form-message  form-message--" + type;
			el.setAttribute("role", "alert");
			el.setAttribute(
				"aria-live",
				type === "error" ? "assertive" : "polite"
			);
			form.appendChild(el);
		}
		el.textContent = text;
	}

	clearMessage(form) {
		form
			.querySelectorAll(".form-message--error")
			.forEach((el) => el.remove());
	}

	destroy() {
		this.widgets.forEach(({ el, widget }) => {
			try {
				widget.destroy();
			} catch (err) {
				/* noop */
			}
			delete el.dataset.frcMounted;
		});
		this.widgets = [];

		this.forms.forEach(({ form, handler }) => {
			form.removeEventListener("submit", handler);
			delete form.dataset.frcBound;
		});
		this.forms = [];
	}
}

function isSolved(value) {
	return (
		typeof value === "string" &&
		value.length > 20 &&
		value.charAt(0) !== UNSOLVED_PREFIX
	);
}
